/**
 * Centralized Hero Data
 * 
 * This file contains all hero descriptions used across the application.
 * Edit descriptions here to update them everywhere: AuthScreen, RoomLobby, 
 * ZoneInfoModal, HeroClassChoiceModal, and ProfileScreen.
 */

// Hero avatar imports
import norimAvatar from '../assets/custom/NorimAvatar.png';
import norimCard from '../assets/custom/Norim.png';
import terrikAvatar from '../assets/custom/TerrikAvatar.png';
import terrikCard from '../assets/custom/Terrik.png';
import evelionAvatar from '../assets/custom/EvelionAvatar.png';
import evelionCard from '../assets/custom/Evelion.png';
import corilAvatar from '../assets/custom/CorilAvatar.png';
import corilCard from '../assets/custom/Coril.png';
import astridAvatar from '../assets/custom/AstridAvatar.png';
import astridCard from '../assets/custom/Astrid.png';
import ferrimAvatar from '../assets/custom/FerrimAvatar.png';
import ferrimCard from '../assets/custom/Ferrim.png';
import veriskAvatar from '../assets/custom/VeriskAvatar.png';
import veriskCard from '../assets/custom/Verisk.png';
import erokAvatar from '../assets/custom/Erok-BuruAvatar.png';
import erokCard from '../assets/custom/Erok-Buru.png';
import vergAvatar from '../assets/custom/VergAvatar.png';
import vergCard from '../assets/custom/Verg.png';
import nyrixAvatar from '../assets/custom/NyrixAvatar.png';
import nyrixCard from '../assets/custom/Nyrix.png';
import shanuAvatar from '../assets/custom/ShanuAvatar.png';
import shanuCard from '../assets/custom/Shanu.png';
import lorianAvatar from '../assets/custom/LorianAvatar.png';
import lorianCard from '../assets/custom/Lorian.png';
import gemataAvatar from '../assets/custom/GemataAvatar.png';
import gemataCard from '../assets/custom/Gemata.png';
import trifleAvatar from '../assets/custom/TrifleAvatar.png';
import trifleCard from '../assets/custom/Trifle.png';
import axelAvatar from '../assets/custom/AxelAvatar.png';
import axelCard from '../assets/custom/Axel.png';

// Class icon imports
import warriorIcon from '../assets/custom/Warrior.png';
import mageIcon from '../assets/custom/Mage.png';
import occultistIcon from '../assets/custom/Occultist.png';
import { Language, t } from './translations';

export interface HeroInfo {
    id: string;
    name: string;
    desc: string;
    avatar: string;
    card: string;
}

export interface ClassInfo {
    id: string;
    name: string;
    desc: string;
    icon: string;
}

/**
 * Central hero data - single source of truth for all hero information.
 * To add a new hero, add an entry here with all required fields.
 */
export const HERO_DATA: Record<string, HeroInfo> = {
    'NORIM': {
        id: 'NORIM',
        name: 'Norim',
        desc: 'Every third Stash Deck card burned for 8 Essence instead of 4.',
        avatar: norimAvatar,
        card: norimCard
    },
    'TERRIK': {
        id: 'TERRIK',
        name: 'Terrik',
        desc: 'Each prepared color deck card that is discarded at night will grant 3 Essence per card.',
        avatar: terrikAvatar,
        card: terrikCard
    },
    'EVELION': {
        id: 'EVELION',
        name: 'Evelion',
        desc: 'If the opponent loses Health Points from your cards, you restore the same amount of Health Points.',
        avatar: evelionAvatar,
        card: evelionCard
    },
    'CORIL': {
        id: 'CORIL',
        name: 'Coril',
        desc: 'Takes to his hand any Stash Deck card of the opponent cancelled by his counter-actions.',
        avatar: corilAvatar,
        card: corilCard
    },
    'ASTRID': {
        id: 'ASTRID',
        name: 'Astrid',
        desc: 'When discarding three same color cards in one night, deal 2 inevitable damage to a chosen opponent.',
        avatar: astridAvatar,
        card: astridCard
    },
    'FERRIM': {
        id: 'FERRIM',
        name: 'Ferrim',
        desc: 'When using the Stash deck, you can either check the Stash deck cards or the burnt pile of Stash cards.',
        avatar: ferrimAvatar,
        card: ferrimCard
    },
    'VERISK': {
        id: 'VERISK',
        name: 'Verisk',
        desc: "Copying opponent's hero.",
        avatar: veriskAvatar,
        card: veriskCard
    },
    'EROK_BURU': {
        id: 'EROK_BURU',
        name: 'Erok-Buru',
        desc: 'Every 2 points lost from the maximum Health Points gives one extra preparation slot on the table.',
        avatar: erokAvatar,
        card: erokCard
    },
    'VERG': {
        id: 'VERG',
        name: 'Verg',
        desc: 'Every 3 points lost from the opponent\'s maximum Health Points gives one extra preparation slot.',
        avatar: vergAvatar,
        card: vergCard
    },
    'NYRIX': {
        id: 'NYRIX',
        name: 'Nyrix',
        desc: 'Doubles the power of all Green Effects. Opponents "Healing" removes only one Disease instead of all Disease reminders.',
        avatar: nyrixAvatar,
        card: nyrixCard
    },
    'SHANU': {
        id: 'SHANU',
        name: 'Shanu',
        desc: 'Restores full maximum Health Points when using Hibernation effect for two nights in a row.',
        avatar: shanuAvatar,
        card: shanuCard
    },
    'LORIAN': {
        id: 'LORIAN',
        name: 'Lorian',
        desc: 'Able to change its class at the beginning of every new turn',
        avatar: lorianAvatar,
        card: lorianCard
    },
    'GEMATA': {
        id: 'GEMATA',
        name: 'Gemata',
        desc: 'When burning cards from the Stash Deck, Essence is not received. Instead, draw three additional cards from the Draw Pile.',
        avatar: gemataAvatar,
        card: gemataCard
    },
    'TRIFLE': {
        id: 'TRIFLE',
        name: 'Trifle',
        desc: 'When burning a Stash Deck card, draw one card from color decks for each tower you own.',
        avatar: trifleAvatar,
        card: trifleCard
    },
    'AXEL': {
        id: 'AXEL',
        name: 'Axel',
        desc: 'Unused colored cards in preparation slots return to hand, and add a copy from their respective color decks.',
        avatar: axelAvatar,
        card: axelCard
    },
};

/**
 * Central class data - single source of truth for all class information.
 */
export const CLASS_DATA: Record<string, ClassInfo> = {
    'WARRIOR': {
        id: 'WARRIOR',
        name: 'Warrior',
        icon: warriorIcon,
        desc: 'The Warrior is a powerful and flexible class. The first Armory Stash Deck card the Warrior prepares during the day will increase preparation limit by 1.'
    },
    'MAGE': {
        id: 'MAGE',
        name: 'Mage',
        icon: mageIcon,
        desc: 'The Mage is the class that requires the most wisdom. The Mage can play Spell Stash Deck cards from their hand at night without preparation. Spell cards played this way must be discarded into the Stash deck. Also, the Mage is able to save 2 cards out of 5 cards drawn from the Stash Deck each day.'
    },
    'OCCULTIST': {
        id: 'OCCULTIST',
        name: 'Occultist',
        icon: occultistIcon,
        desc: 'The Occultist is strong at every stage of the game. The Occultist pays half the cost for Artifact Stash Deck cards.'
    }
};

/**
 * Get hero info by ID (case-insensitive, handles hyphens/underscores)
 */
export function getHeroInfo(heroId: string, lang: Language = 'en'): HeroInfo | undefined {
    const normalizedId = heroId.toUpperCase().replace(/-/g, '_');
    const hero = HERO_DATA[normalizedId];
    if (hero) {
        return {
            ...hero,
            name: t(`hero.${normalizedId}.name`, lang),
            desc: t(`hero.${normalizedId}.desc`, lang)
        };
    }
    return undefined;
}

/**
 * Get hero description by ID
 */
export function getHeroDescription(heroId: string, lang: Language = 'en'): string {
    return getHeroInfo(heroId, lang)?.desc || 'Unknown hero.';
}

/**
 * Get hero description by name (for components that use hero names instead of IDs)
 */
export function getHeroDescriptionByName(heroName: string, lang: Language = 'en'): string {
    const normalizedId = heroName.toUpperCase().replace(/[\s-]/g, '_');
    const heroId = Object.keys(HERO_DATA).find(id => id === normalizedId);
    if (heroId) {
        return t(`hero.${heroId}.desc`, lang);
    }
    return 'Unknown hero.';
}

/**
 * Get hero name by ID
 */
export function getHeroName(heroId: string, lang: Language = 'en'): string {
    const normalizedId = heroId.toUpperCase().replace(/-/g, '_');
    return t(`hero.${normalizedId}.name`, lang) || heroId;
}

/**
 * Get all heroes as array (for iteration)
 */
export function getAllHeroes(): HeroInfo[] {
    return Object.values(HERO_DATA);
}

/**
 * Get class info by ID
 */
export function getClassInfo(classId: string, lang: Language = 'en'): ClassInfo | undefined {
    const normalizedId = classId.toUpperCase();
    const classInfo = CLASS_DATA[normalizedId];
    if (classInfo) {
        return {
            ...classInfo,
            name: t(`class.${normalizedId}.name`, lang),
            desc: t(`class.${normalizedId}.desc`, lang)
        };
    }
    return undefined;
}

/**
 * Get class description by ID
 */
export function getClassDescription(classId: string, lang: Language = 'en'): string {
    return getClassInfo(classId, lang)?.desc || 'Unknown class.';
}

